/**
 * Unified analytics-consent persistence + the native-iOS launch flow.
 *
 * One localStorage key records the user's consent choice. It is read in two
 * places that must agree: the inline bootstrap in `index.html` (re-applies the
 * choice within GTM Consent Mode's `wait_for_update` window, skipping `ad_*`
 * on iOS) and the React layer (ConsentBanner / the iOS launch flow below).
 *
 * On the native iOS shell there is no cookie banner: Apple's App Tracking
 * Transparency dialog, requested at first launch, is the only consent surface
 * (Guideline 5.1.2(i) — App Review must observe the ATT prompt, and flagged
 * our cookie prompt as implying tracking). First-party analytics (GA4
 * `analytics_storage` + PostHog) need no permission under Apple's rules and
 * are granted by default — the service is US-only, so no ePrivacy/GDPR opt-in
 * obligation applies; revisit if the app ships to those territories.
 */
import { updateConsent } from './gtm'
import { safeStorageGet, safeStorageSet } from './safe-storage'

export const CONSENT_STORAGE_KEY = 'stopal.analytics-consent'

export type ConsentChoice = 'granted' | 'denied'

export function readConsentChoice(): ConsentChoice | null {
  const v = safeStorageGet(CONSENT_STORAGE_KEY)
  return v === 'granted' || v === 'denied' ? v : null
}

/** Persist the choice; storage failures (private mode) degrade to session-only. */
export function storeConsentChoice(choice: ConsentChoice): void {
  safeStorageSet(CONSENT_STORAGE_KEY, choice)
}

/**
 * Apply consent on the native iOS shell, every launch.
 *
 * Analytics are granted (and persisted) immediately: the grant must land
 * within GTM Consent Mode's `wait_for_update` window (2s, set in
 * `index.html`) or the launch's first page_view fires as denied — while the
 * ATT request can block far longer (Apple's dialog waits on the user, and
 * the app must be foreground-visible before it can even be presented).
 *
 * `requestAds` resolves whether advertising signals are permitted — the ATT
 * request (`requestAdTrackingConsent`), which shows Apple's dialog only while
 * the status is `notDetermined` and otherwise returns the current status, so
 * one path covers first launch, later launches, and revocation via iOS
 * Settings. `ad_*` stays denied until it resolves true.
 *
 * `optInAnalytics` is injected (PostHog opt-in) so this module stays free of
 * the posthog-js browser dependency and testable under the node environment.
 */
export async function applyIosLaunchConsent(
  requestAds: () => Promise<boolean>,
  optInAnalytics: () => void,
): Promise<void> {
  updateConsent({ analytics: true, ads: false })
  // Persisted so the index.html inline re-apply grants analytics_storage
  // within the Consent Mode window on the next launch (it skips ad_* on iOS;
  // this flow re-derives ad_* from ATT every launch).
  storeConsentChoice('granted')
  try {
    optInAnalytics()
  } catch {
    // PostHog opt-in must not block the ad-signal upgrade below; callers
    // fire-and-forget this promise, so a throw here would surface only as
    // an unhandled rejection.
  }
  const ads = await requestAds()
  if (ads) {
    updateConsent({ analytics: true, ads: true })
  }
}
